import React, { createContext, useContext, useEffect, useState, PropsWithChildren } from 'react';
import { User as FirebaseUser, onAuthStateChanged } from 'firebase/auth';
import { appFirebaseAuth } from './firebase/firebase';
import { usersService, SupabaseUser } from '../services/supabase';

interface AuthContextType {
  firebaseUser: FirebaseUser | null;
  supabaseUser: SupabaseUser | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [supabaseUser, setSupabaseUser] = useState<SupabaseUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(appFirebaseAuth, async (user) => {
      setFirebaseUser(user);

      if (user) {
        try {
          // Sync user data to Supabase
          // Note: The Supabase client automatically includes the Firebase JWT
          // via the accessToken function configured in supabase.ts
          const supabaseUserData = await usersService.upsertUser({
            id: user.uid,
            email: user.email!,
            display_name: user.displayName,
            photo_url: user.photoURL,
          });

          setSupabaseUser(supabaseUserData);
        } catch (error) {
          console.error('Error syncing user with Supabase:', error);
        }
      } else {
        setSupabaseUser(null);
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const signOut = async () => {
    await appFirebaseAuth.signOut();
    setFirebaseUser(null);
    setSupabaseUser(null);
  };

  return (
    <AuthContext.Provider value={{ firebaseUser, supabaseUser, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
